import api from "./client";
import type { InAppNotification } from "@store/inAppNotificationsStore";

/** "off" = never, "assigned" = only if stakeholder, "always" = broadcast */
export type NotifPrefValue = "off" | "assigned" | "always";

export type NotificationPreferencesPayload = {
  event_types: Record<string, NotifPrefValue> | null;
  ordre_status_filters: string[] | null;
};

export type OrgMemberPreferences = {
  user_id: string;
  display_name: string;
  photo_url: string;
  notification_preferences: NotificationPreferencesPayload;
};

export type ServerNotification = {
  id: string;
  user_id: string;
  org_id: string;
  type: string;
  title: string;
  body: string;
  href: string;
  read: boolean;
  created_at: string;
};

/** Convert server notification to the frontend InAppNotification shape. */
function toInApp(n: ServerNotification): InAppNotification {
  return {
    id: n.id,
    type: n.type as InAppNotification["type"],
    title: n.title,
    body: n.body,
    href: n.href,
    read: n.read,
    createdAt: n.created_at,
  };
}

export const notificationsApi = {
  fetchAll: async (): Promise<InAppNotification[]> => {
    const { data } = await api.get<ServerNotification[]>("/notifications");
    return data.map(toInApp);
  },

  markRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await api.post("/notifications/mark-all-read");
  },

  dismiss: async (id: string): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },

  clearAll: async (): Promise<void> => {
    await api.delete("/notifications");
  },

  // ── Notification preferences ──

  getPreferences: async (): Promise<NotificationPreferencesPayload> => {
    const { data } = await api.get<NotificationPreferencesPayload>("/notifications/preferences");
    return data;
  },

  updatePreferences: async (prefs: NotificationPreferencesPayload): Promise<void> => {
    await api.put("/notifications/preferences", prefs);
  },

  getOrgPreferences: async (): Promise<OrgMemberPreferences[]> => {
    const { data } = await api.get<OrgMemberPreferences[]>("/notifications/preferences/org");
    return data;
  },

  updateUserPreferences: async (userId: string, prefs: NotificationPreferencesPayload): Promise<void> => {
    await api.put(`/notifications/preferences/${userId}`, prefs);
  },
};
